/**
 * World-point resolution for AI-finding jumps (#921).
 *
 * `aiJumpTarget.ipp` is the canonical sidecar's per-instance
 * ImagePositionPatient, which the backend SYNTHESIZES as
 * `z_position * slice_normal` (web-demo-backend canonical_metadata.py) because
 * the canonicalize pipeline discarded the real IPP. That synthesis drops the
 * slice's in-plane origin, so the point is displaced along the slice normal by
 * `origin . normal` — ~19mm (5 slices) on the gantry-tilted study in #921 — and
 * a `jumpToWorld` at it lands on the wrong slice. The volume's actual geometry
 * comes from the REAL per-frame IPP Cornerstone parses out of the DICOM
 * headers, so the jump must aim in that frame: prefer Cornerstone's
 * per-imageId imagePlaneModule (the same authority the index-navigation path
 * uses — `sliceWorldPositionFromMetadata`, #401/#544) and use the sidecar
 * value only when the target frame's metadata isn't parsed yet.
 */
import type { AiJumpTarget, Vec3 } from "../atoms/positionAtoms";
import { logger } from "../utils/logger";
import {
  sliceWorldPositionFromMetadata,
  type CornerstoneMetaDataLike,
} from "./volumeViewport.sliceWorldPosition";

export type AiJumpWorldSource = "centroid" | "cornerstone" | "sidecar";

export interface ResolvedAiJumpWorld {
  world: [number, number, number];
  source: AiJumpWorldSource;
}

type AiJumpWorldInput = Pick<AiJumpTarget, "ipp" | "seriesId" | "sliceIndex">;

/**
 * Slice-plane world point for the target's `sliceIndex`: Cornerstone's real
 * per-imageId IPP when available, else the sidecar `ipp` (correct on studies
 * whose sidecar carries real IPP; displaced on canonical synthesized ones —
 * hence the warn, so a wrong-plane landing is traceable in ops).
 */
export function resolveAiJumpWorld(
  ai: AiJumpWorldInput,
  metaData: CornerstoneMetaDataLike,
  imageIds: readonly string[],
): ResolvedAiJumpWorld {
  const imageId =
    Number.isInteger(ai.sliceIndex) && ai.sliceIndex >= 0 ? imageIds[ai.sliceIndex] : undefined;
  const real = sliceWorldPositionFromMetadata(metaData, imageId);
  if (real) return { world: real, source: "cornerstone" };
  if (imageId) {
    logger.warn(
      "ai_jump_world_sidecar_fallback seriesId=%s sliceIndex=%d reason=imageplane_uncached",
      ai.seriesId,
      ai.sliceIndex,
    );
  } else {
    logger.debug(
      "[aiJumpWorld] no imageId for seriesId=%s sliceIndex=%d (listLen=%d) — sidecar ipp",
      ai.seriesId,
      ai.sliceIndex,
      imageIds.length,
    );
  }
  return { world: [ai.ipp[0], ai.ipp[1], ai.ipp[2]], source: "sidecar" };
}

function isIppCornerFallback(centerWorld: Vec3, ipp: Vec3): boolean {
  return centerWorld[0] === ipp[0] && centerWorld[1] === ipp[1] && centerWorld[2] === ipp[2];
}

/**
 * Center-point variant for consumers of `centerWorld` (the locator pulse and
 * the in-slice centroid jump). A genuine centroid (minted by flows that HAVE
 * an in-slice point, e.g. lesion selection) is already real world space — keep
 * it. AI findings carry no in-slice point, so `selectFinding` falls
 * `centerWorld` back to the ipp corner verbatim; detect that case by equality
 * and route it through the same real-geometry preference as the jump.
 */
export function resolveAiJumpCenter(
  ai: AiJumpWorldInput & Pick<AiJumpTarget, "centerWorld">,
  metaData: CornerstoneMetaDataLike,
  imageIds: readonly string[],
): ResolvedAiJumpWorld {
  if (!isIppCornerFallback(ai.centerWorld, ai.ipp)) {
    return {
      world: [ai.centerWorld[0], ai.centerWorld[1], ai.centerWorld[2]],
      source: "centroid",
    };
  }
  return resolveAiJumpWorld(ai, metaData, imageIds);
}
